import { useEffect, useState } from 'react';
import type { AppSession } from '../lib/types';
import { startOrgCreation, type AuthConfig } from '../lib/home';
import { ApiError, api } from '../lib/api';
import { Card } from '../components/Bits';
import { HeartMark, Icon } from '../components/HeartMark';

/**
 * Found a practice. The `.org` Smart Agent is created by the person's Home (`org-create`), so
 * HeartCoach never holds a key for it; what HeartCoach records is that the practice exists and who
 * stewards it. On a dev deployment a label stands in for the organization address.
 */
export function PracticeSetup({ session, config, onDone, orgReturn, clearOrgReturn, onBack }: { session: AppSession; config: AuthConfig | null; onDone: () => Promise<void>; orgReturn: { code: string; state: string } | null; clearOrgReturn: () => void; onBack: () => void }) {
  const [name, setName] = useState('');
  const [label, setLabel] = useState('');
  const [org, setOrg] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canCeremony = Boolean(config?.home.origin && config.home.zone !== 'localhost' && session.via !== 'dev');

  useEffect(() => {
    if (orgReturn) clearOrgReturn();
  }, [orgReturn, clearOrgReturn]);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const ref = org.trim() || label.trim();
      if (!ref || !name.trim()) throw new Error('Give the practice a name, and either its organization address or a label.');
      await api.createPractice({ org: ref, name: name.trim(), ...(label.trim() ? { orgName: `${label.trim().toLowerCase()}.org` } : {}) }, session.token);
      await onDone();
      location.hash = '#/practice';
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="app">
      <header className="topbar">
        <div className="topbar-inner">
          <span className="brand">
            <HeartMark size={22} />
            <span>HeartCoach</span>
          </span>
          <span style={{ flex: 1 }} />
          <button type="button" className="quiet" onClick={onBack} style={{ color: 'rgba(255,255,255,0.75)' }}>
            <Icon name="back" size={16} /> Back
          </button>
        </div>
      </header>

      <main className="shell shell-narrow">
        <div className="page-head">
          <div>
            <span className="eyebrow">For practices</span>
            <h1>Set up your practice</h1>
            <span className="hint">Your organization is created and custodied at your Home. HeartCoach only records that it exists.</span>
          </div>
        </div>

        <div className="stack">
          <Card>
            <h2>Your practice</h2>
            <label className="field">
              <span>Practice name</span>
              <input type="text" value={name} maxLength={60} placeholder="Atria Heart" onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="field">
              <span>Organization label</span>
              <input type="text" value={label} maxLength={32} placeholder="atria-heart" onChange={(e) => setLabel(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} />
              <small className="hint small">Becomes {label ? `${label}.org` : '<label>.org'}, the practice's own Smart Agent name.</small>
            </label>
          </Card>

          <Card>
            <h2>The organization</h2>
            {canCeremony ? (
              <>
                <p className="hint">Your Home creates the practice's organization Smart Agent and makes you its steward. HeartCoach never holds its key.</p>
                <button
                  className="primary"
                  type="button"
                  disabled={busy || !label.trim() || !config}
                  onClick={async () => {
                    if (!config) return;
                    setBusy(true);
                    try {
                      location.href = await startOrgCreation(config, label.trim(), `${name.trim() || label} — a cardiology practice on HeartCoach`);
                    } catch (e) {
                      setError(e instanceof Error ? e.message : String(e));
                      setBusy(false);
                    }
                  }}
                >
                  Create the organization at your Home
                </button>
                <label className="field">
                  <span>Organization address (once your Home shows it)</span>
                  <input type="text" value={org} className="mono" placeholder="0x…" onChange={(e) => setOrg(e.target.value)} />
                </label>
              </>
            ) : (
              <p className="hint">On this development deployment there is no Home to run the org-create ceremony, so the label stands in for the organization address.</p>
            )}
          </Card>

          {error ? <p className="form-error">{error}</p> : null}
          <div className="pair">
            <button className="primary big" type="button" disabled={busy || !name.trim() || (!org.trim() && !label.trim())} onClick={() => void create()}>
              {busy ? 'Recording…' : 'Record the practice'}
            </button>
            <span className="hint small">You become its first clinical manager.</span>
          </div>
        </div>
      </main>
    </div>
  );
}
